import { Select } from '../../components/ui';
import { useAddLeadsToList } from './hooks';
import { listAddMessage, splitForList } from './leadListSafety';
import type { LeadClosureReason } from './leadFields';
import type { Lead } from './types';

/**
 * Put the selected leads into a campaign list, from the Leads table.
 *
 * ── The gap this closes ────────────────────────────────────────────────────
 *
 * Kyu described a loop: "we can see those people and set up campaigns, put
 * them in a list, send emails again... basic sales." Measured before Drop 38,
 * it broke at step three. `useAddLeadsToList` existed and worked, but the only
 * door to it was the Lists tab — open a list, search leads by NAME, tick up to
 * fifty. There was no way to take a filtered set on the Leads table and put it
 * anywhere. So somebody filtering "Not right now, closed in the last 12
 * months" got a correct list and no way to act on it except copying names onto
 * paper, and a filter that leads nowhere is a report, not a tool.
 *
 * ── Why it is a dropdown and not a button ──────────────────────────────────
 *
 * There is no sensible default list, and picking the wrong one is the failure
 * mode: a re-engagement cohort dropped into an active outbound list is emailed
 * tomorrow morning. A dropdown makes the destination the decision, which is
 * what it actually is. It returns to "Add to list…" afterwards, so the control
 * never reads as a filter that is switched on.
 *
 * ── The guard is the point, not a nicety ───────────────────────────────────
 *
 * Do-not-contact leads never go in — see leadListSafety.ts for why consent and
 * deliverability are handled differently — and every number is said out loud
 * afterwards. The picker is disabled until the closing-reason vocabulary has
 * loaded, because until then there is no honest answer to which leads must be
 * kept out, and a guard that quietly gives up is worse than no guard at all:
 * it is a guard everybody believes in.
 */
export function AddSelectedToList({
  leads,
  selectedIds,
  lists,
  closureReasons,
  onDone,
  onError,
}: {
  leads: Lead[];
  selectedIds: Set<string>;
  lists: { id: string; name: string }[];
  /** Empty until the vocabulary loads, which disables the control. */
  closureReasons: LeadClosureReason[];
  /** Called with what happened, and with the ids actually added. */
  onDone: (message: string, added: string[]) => void;
  onError: (message: string) => void;
}) {
  const add = useAddLeadsToList();
  const split = splitForList(leads, selectedIds, closureReasons);

  if (lists.length === 0) {
    return (
      <span className="tracker-field-note">
        No lists yet — make one on the Lists tab and this selection can go into it.
      </span>
    );
  }

  return (
    <Select
      label="Add to list"
      labelHidden
      value=""
      // Null split means the vocabulary has not loaded. Disabled rather than
      // hidden: a control that vanishes and returns looks like a bug, and this
      // one comes back within a second.
      disabled={split === null || add.isPending}
      onChange={(event) => {
        const list = lists.find((entry) => entry.id === event.target.value);
        if (list === undefined || split === null) {
          return;
        }
        if (split.allowed.length === 0) {
          onError(
            split.blocked === 0
              ? 'Nothing selected to add.'
              : `Nothing was added. All ${String(split.blocked)} selected are marked Do not contact.`,
          );
          return;
        }
        onError('');
        add.mutate(
          { listId: list.id, leadIds: split.allowed },
          {
            // `added` is what the upsert actually inserted, so leads already on
            // the list are not counted twice — listAddMessage turns the
            // difference into "already on it" rather than overstating the work.
            onSuccess: (added) => {
              onDone(listAddMessage(added, split, list.name), split.allowed);
            },
            onError: (cause) => {
              onError(cause.message);
            },
          },
        );
      }}
      options={[
        { value: '', label: add.isPending ? 'Adding…' : 'Add to list…' },
        ...lists.map((list) => ({ value: list.id, label: list.name })),
      ]}
    />
  );
}
